import { useEffect, useRef, useState } from 'react';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ProcessEntity, saveTypeEnum, ToastType } from '../../../../../../common/enums';
import { getJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form } from '../../../../elements/common';
import { EvaluationRequestItem } from '../../../evaluation/EvaluationRequestItem';
import { addFormAnalytics, addFormConversion } from '../../utils/formAnalytics';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { OrthCV1RequestAddEditInner } from './OrthCV1RequestAddEditInner';

export function OrthCV1RequestAddEdit(props: ICustomControlRequestAddEditProps): JSX.Element {
    const [showEvaluation, setShowEvaluation] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState<boolean>();
    const [requestId, setRequestId] = useState<string>();

    const formRef = useRef<HTMLFormElement>(null);
    const saveSendButtonRef = useRef<HTMLButtonElement>(null);
    const {
        errors,
        handleChangeCustomControl,
        handleChange,
        itemState,
        isFirstItem,
        onSaveRequest,
        onSave,
        onCancel,
        process,
        request,
        service,
        validateOneCustomControl,
        validateRequest,
        validateOne,
    } = props;

    useEffect(() => {
        handleChangeCustomControl({ ...OrthCV1RequestInitializeCustomControlData(), ...request.customControlData });
        addFormAnalytics(formRef.current, saveSendButtonRef.current);
    }, []);

    return (
        <NewOrEditRequestProcessContainer
            isFirstItem={isFirstItem}
            itemState={itemState}
            process={process}
            service={service}
            title={getServiceLabel(service.code, 'request', service.name)}
        >
            <Form
                formRef={formRef}
                id="OrthCV1RequestAddEditId"
                name="OrthCV1RequestAddEdit"
                onSubmit={() => null}
            >
                {request.customControlData && (
                    <OrthCV1RequestAddEditInner
                        errors={errors}
                        question={getQuestionPropsForAddEditInner()}
                        selectedDiagnosticReportProvider={getSelectedDiagnosticReportProviderPropsForAddEditInner()}
                        serviceId={service.id}
                        toelichting={getToelichtingPropsForAddEditInner()}
                    />
                )}
                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={onCancel}
                            entity={ProcessEntity.request}
                            isExisting={!!request.id}
                            serviceCode={service.code}
                            serviceType={service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={buttonDisabled}
                            save={async () => {
                                await saveRequest(saveTypeEnum.draft);
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={buttonDisabled}
                            saveSend={async () => {
                                await saveRequest(saveTypeEnum.final);
                            }}
                            saveSendButtonRef={saveSendButtonRef}
                        />,
                    ]}
                />
            </Form>
            {showEvaluation && (
                <EvaluationRequestItem
                    onClose={async () => {
                        await handleEvaluationSaved();
                    }}
                    requestId={requestId}
                />
            )}
        </NewOrEditRequestProcessContainer>
    );

    function getSelectedDiagnosticReportProviderPropsForAddEditInner() {
        return {
            selectedDiagnosticReportProviderPractitionerId: request.selectedDiagnosticReportProviderPractitionerId,
            selectedDiagnosticReportProviderOrganizationId: request.selectedDiagnosticReportProviderOrganizationId,
            handleChange: async (organizationId: string | null, practitionerId: string | null) => {
                await handleChange('selectedDiagnosticReportProviderOrganizationId', organizationId);
                await handleChange('selectedDiagnosticReportProviderPractitionerId', practitionerId);
            },
            validateOne: () => validateOne('selectedDiagnosticReportProviderOrganizationId'),
        };
    }

    function getQuestionPropsForAddEditInner() {
        return {
            value: request.question,
            error: errors.get('question'),
            handleChange: async (value: string | null) => await handleChange('question', value),
            validateOne: () => validateOne('question'),
        };
    }

    function getToelichtingPropsForAddEditInner() {
        return {
            value: request.customControlData.toelichting,
            error: errors.get('customControlData.toelichting'),
            handleChange: async (value: string | null) => await handleChangeCustomControl({ toelichting: value }),
            validateOne: () => validateOneCustomControl('toelichting'),
        };
    }

    async function handleEvaluationSaved(): Promise<void> {
        const result = await getJson(`/api/organizations/${request.selectedDiagnosticReportProviderOrganizationId}`);
        showServiceToast(
            ToastType.success,
            'toast.submitSuccess',
            service,
            'request',
            translateKey('toast.consultRequestSubmit', result.json.name)
        );
        onSave(requestId as string);
        addFormConversion(formRef.current);
    }

    async function saveRequest(saveType: saveTypeEnum): Promise<void> {
        setButtonDisabled(true);

        const validatedRequest = await validateRequest(saveType);
        if (!validatedRequest) {
            setButtonDisabled(false);

            return;
        }

        const savedRequestId = await onSaveRequest(saveType);
        setRequestId(savedRequestId);

        if (saveType === saveTypeEnum.draft) {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
            onSave(savedRequestId);
        } else {
            setShowEvaluation(true);
        }
    }
}

function OrthCV1RequestInitializeCustomControlData() {
    return {
        toelichting: null,
        evaluation: {
            otherwiseReferPatient: null,
        },
    };
}
